import { useCallback } from "react";
import { API_BASE_URL } from "../constants/app.constant";
import { useFetch } from "../hooks/useFetch";
import { APIResponse } from "../interfaces/common.interface";
import { LiveResponseData, LiveResponseItem, BackendLiveResponseItem, LiveResponseMetrics } from "../interfaces/live-response.interface";
import useAuthService from "./auth.service";

const transformBackendResponse = (backendItem: BackendLiveResponseItem): LiveResponseItem => {
  const detailedMetrics = backendItem.detailed_metrics || {};
  
    const metrics: LiveResponseMetrics = {
    clarity: detailedMetrics.hallucination?.score ?? 0,
    faithfulness: detailedMetrics.faithfulness?.score ?? 0,
    coherence: detailedMetrics.coherence?.score ?? 0,
    "c. relevancy": detailedMetrics.contextual_relevancy?.score ?? 0,
    "a. relevancy": detailedMetrics.answer_relevancy?.score ?? 0
  };
 
  const metricValues = Object.values(metrics).filter((v) => v > 0);
  const averageScore = backendItem.overall_score ?? 0;

  return {
    id: String(backendItem.execution_id),
    query: backendItem.query || "",
    response: backendItem.response || "",
    metrics,
    averageScore,
    userFeedback: backendItem.feedback || null,
    chatId: String(backendItem.message_id || ""),
    model: backendItem.model || "",
    responseTime: backendItem.response_time ?? 0,
    latency: backendItem.latency ?? undefined,
  };
};

/**
 * useLiveResponseService hook provides live response evaluation API service functions.
 * Handles fetching live response evaluations with pagination, sorting, and data transformation.
 * 
 * @hook
 * @returns {Object} Live response service methods
 * @returns {Function} returns.getLiveResponses - Get live response evaluations
 * 
 * @example
 * ```tsx
 * const { getLiveResponses } = useLiveResponseService();
 * 
 * const responses = await getLiveResponses(1, 5, 'averageScore', 'desc');
 * ```
 */
const useLiveResponseService = () => {
  const fetchService = useFetch();
  const { getUserAccessToken } = useAuthService();

  const LIVE_RESPONSE_API = `${API_BASE_URL}/admin/live-response`;

  /**
   * Gets live response evaluations with pagination and sorting.
   * Transforms backend response format to frontend format with metrics.
   * 
   * @param {number} [page=1] - Page number
   * @param {number} [pageSize=5] - Page size
   * @param {string} [sortBy] - Field to sort by
   * @param {"asc" | "desc"} [sortOrder] - Sort order
   * @param {AbortSignal} [signal] - Optional abort signal for request cancellation
   * @returns {Promise<{data: LiveResponseItem[]; pagination: PaginationInfo} | undefined>} Transformed response data with pagination
   * @throws {Error} If token is invalid or response structure is invalid
   */
  const getLiveResponses = useCallback(
    async (
      page: number = 1,
      pageSize: number = 5,
      sortBy?: string,
      sortOrder?: "asc" | "desc",
      signal?: AbortSignal
    ) => {
      try {
        const token = await getUserAccessToken();
        if (!token) throw new Error("Invalid token");

        // Build query params
        const params = new URLSearchParams({
          page: page.toString(),
          size: pageSize.toString(),
        });

        if (sortBy) {
          params.append("sortBy", sortBy);
        }

        if (sortOrder) {
          params.append("sortOrder", sortOrder);
        }

        const response = await fetchService.get<APIResponse<BackendLiveResponseItem[]>>(
          `${LIVE_RESPONSE_API}?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            signal,
          }
        );

        const backendDataArray = response.data;
        
        if (!backendDataArray || !Array.isArray(backendDataArray)) {
          console.error("Invalid response structure:", response);
          throw new Error("Invalid response structure from API");
        }

        const transformedData: LiveResponseItem[] = backendDataArray.map(transformBackendResponse);

        return {
          data: transformedData,
          pagination: response.pagination,
        };
      } catch (error) {
        console.warn("API not available", error);
      }
    },
    [fetchService, getUserAccessToken]
  );

  return {
    getLiveResponses,
  };
};

export default useLiveResponseService;

